import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/app/common";
import { LmLogisticsInfo } from "@/components/order/LogisticsBudgetPanel";
import { DataTable } from "@/components/app/DataTable";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { QcFileList, QcFilePicker, QcFilesDialog, uploadQcFiles } from "@/components/planning/QcFiles";

const DECIDE = [["approved", "Approve - release for movement"], ["on_hold", "On hold"], ["rejected", "Reject"]];
const decidable = (s) => ["ready_for_qc", "on_hold"].includes(s.qc_status);

const qcCols = (onFiles) => [
  { key: "submitted_at", label: "Submitted", render: (r) => fmtDateTime(r.submitted_at) },
  { key: "lm_number", label: "LM no.", render: (r) => <Link className="font-mono text-sky-700 hover:underline" to={`/orders/${r.lm_id}`} onClick={(e) => e.stopPropagation()}>{r.lm_number}</Link> },
  { key: "customer_name", label: "Customer", wrap: true },
  { key: "scope", label: "Scope", value: (r) => (r.qc_scope_type === "FullLM" ? "Full LM" : r.planned_dispatch_numbers.join(", ")), render: (r) => (r.qc_scope_type === "FullLM" ? <b>Full LM</b> : <span className="font-mono text-xs">{r.planned_dispatch_numbers.join(", ")}</span>) },
  { key: "expected_dispatch_date", label: "Expected dispatch", render: (r) => fmtDate(r.expected_dispatch_date) },
  { key: "item_summary", label: "Items", wrap: true },
  { key: "qty", label: "Qty", right: true },
  { key: "by", label: "Submitted by", value: (r) => r.submitted_by?.name, render: (r) => r.submitted_by?.name },
  { key: "qc_status", label: "QC status", render: (r) => <StatusBadge status={r.qc_status} testid={`qc-status-${r.id}`} /> },
  { key: "qc_remarks", label: "QC remarks", wrap: true, value: (r) => r.qc_remarks, render: (r) => <span>{r.qc_remarks}{r.qc_checked_by && <span className="block text-[11px] text-slate-500">{r.qc_checked_by.name} · {fmtDateTime(r.qc_checked_at)}</span>}</span> },
  { key: "qc_file_count", label: "QC files", right: true, value: (r) => r.qc_file_count || 0,
    render: (r) => (r.qc_file_count ? <button className="text-sky-700 hover:underline" onClick={(e) => { e.stopPropagation(); onFiles(r); }} data-testid={`qc-files-open-${r.id}`}>{r.qc_file_count} file{r.qc_file_count > 1 ? "s" : ""}</button> : "—") },
];

function DecisionDialog({ s, onClose, onDone }) {
  const [st, setSt] = useState("approved");
  const [rem, setRem] = useState("");
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState([]);
  const [uploaded, setUploaded] = useState(0);
  const { data: lm } = useFetch(`/orders/${s.lm_id}`);
  const go = async () => {
    if (st !== "approved" && rem.trim().length < 3) {
      toast.error("QC remarks are required when rejecting or holding");
      return;
    }
    setBusy(true);
    try {
      if (picked.length) {
        await uploadQcFiles(s.id, picked, (kind) => { setPicked((cur) => cur.filter((p) => p.kind !== kind)); setUploaded((n) => n + 1); });
      }
      const r = await api.post(`/qc-submissions/${s.id}/decision`, { qc_status: st, qc_remarks: rem });
      const mail = r.data.email;
      toast.success(`QC ${st.replace("_", " ")}${mail ? ` · email ${mail.status.replace(/_/g, " ")}${mail.to?.length ? ` to ${mail.to.length}` : ""}` : ""}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="qc-decision-dialog">
        <DialogHeader><DialogTitle>QC review · {s.lm_number}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-600 space-y-1">
          <div><b>{s.qc_scope_type === "FullLM" ? "Full LM" : s.planned_dispatch_numbers.join(", ")}</b> · {s.customer_name}</div>
          <div>{s.item_summary}</div>
          {s.remarks && <div>Production remarks: {s.remarks}</div>}
          {s.qc_status === "on_hold" && <div className="text-amber-800">On hold: {s.qc_remarks}</div>}
        </div>
        {lm && <LmLogisticsInfo ctx={lm} testid="qc-lm-logistics" />}
        <div className="grid gap-2" data-testid="qc-decision-options">
          {DECIDE.map(([k, l]) => (
            <label key={k} className={`flex items-center gap-2 rounded border px-3 py-2 text-sm cursor-pointer ${st === k ? "border-sky-500 bg-sky-50" : "border-slate-200"}`}>
              <input type="radio" name="qc" checked={st === k} onChange={() => setSt(k)} data-testid={`qc-opt-${k}`} />{l}
            </label>
          ))}
        </div>
        <div className="space-y-2" data-testid="qc-files-section">
          <Label>QC documents & pictures</Label>
          {(s.qc_file_count > 0 || uploaded > 0) && <QcFileList key={uploaded} sid={s.id} testid="qc-existing-files" />}
          <QcFilePicker picked={picked} setPicked={setPicked} />
        </div>
        <div><Label>QC remarks {st !== "approved" && "*"}</Label><Textarea rows={3} value={rem} onChange={(e) => setRem(e.target.value)} data-testid="qc-remarks-input" /></div>
        {st === "rejected" && <p className="text-xs text-red-700">Rejection emails the LM creator, approver, submitter and LM owners.</p>}
        <DialogFooter><Button disabled={busy} onClick={go} data-testid="qc-decision-save">{busy && picked.length ? "Uploading files…" : "Save QC decision"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QcQueue({ status = "", lmId = "", testid = "qc-queue", exportName = "QC_Register", onChanged }) {
  const { can } = useMe();
  const { data, reload } = useFetch(`/qc-submissions?qc_status=${status}${lmId ? `&lm_id=${lmId}` : ""}`);
  const [pick, setPick] = useState(null);
  const [filesOf, setFilesOf] = useState(null);
  const canDecide = can("qc.approve");
  return (
    <>
      <DataTable testid={testid} columns={qcCols(setFilesOf)} rows={data} onRowClick={canDecide ? (r) => decidable(r) && setPick(r) : undefined}
        rowClickable={decidable} exportName={exportName} exportPerm="qc.export" empty="Nothing here" />
      {filesOf && <QcFilesDialog s={filesOf} onClose={() => setFilesOf(null)} />}
      {pick && <DecisionDialog s={pick} onClose={() => setPick(null)} onDone={() => { setPick(null); reload(); onChanged?.(); }} />}
    </>
  );
}
